import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";
import { Layout } from "@/components/Layout";

const searchSchema = z.object({ token: z.string().optional() });

export const Route = createFileRoute("/unsubscribe")({
  validateSearch: (s) => searchSchema.parse(s),
  head: () => ({
    meta: [
      { title: "Peruuta sähköpostit | Montessori24" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: UnsubscribePage,
});

type State =
  | "loading"
  | "confirm"
  | "already"
  | "invalid"
  | "done"
  | "error";

function UnsubscribePage() {
  const { token } = useSearch({ from: "/unsubscribe" });
  const [state, setState] = useState<State>("loading");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setState("invalid");
      return;
    }
    let cancelled = false;
    fetch(`/email/unsubscribe?token=${encodeURIComponent(token)}`)
      .then(async (r) => {
        const body = (await r.json().catch(() => ({}))) as {
          valid?: boolean;
          reason?: string;
        };
        if (cancelled) return;
        if (r.ok && body.valid) setState("confirm");
        else if (body.reason === "already_unsubscribed") setState("already");
        else setState("invalid");
      })
      .catch(() => {
        if (!cancelled) setState("error");
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const onConfirm = async () => {
    if (!token || busy) return;
    setBusy(true);
    try {
      const r = await fetch("/email/unsubscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const body = (await r.json().catch(() => ({}))) as {
        success?: boolean;
        reason?: string;
      };
      if (r.ok && body.success) setState("done");
      else if (body.reason === "already_unsubscribed") setState("already");
      else setState("error");
    } catch {
      setState("error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <section className="container-prose py-24 md:py-32">
        <div className="mx-auto max-w-xl text-center">
          {state === "loading" && (
            <>
              <h1 className="font-display text-4xl text-foreground">
                Tarkistetaan linkkiä…
              </h1>
              <p className="mt-4 text-muted-foreground">Odota hetki.</p>
            </>
          )}

          {state === "confirm" && (
            <>
              <h1 className="font-display text-4xl text-foreground">
                Peruuta sähköpostit
              </h1>
              <p className="mt-4 text-muted-foreground">
                Vahvistamalla peruutuksen emme lähetä sinulle enää
                sähköpostiviestejä Montessori24:ltä.
              </p>
              <button
                type="button"
                onClick={onConfirm}
                disabled={busy}
                className="mt-8 inline-flex rounded-full bg-clay px-6 py-3 text-sm font-medium text-white disabled:opacity-70"
              >
                {busy ? "Peruutetaan…" : "Vahvista peruutus"}
              </button>
            </>
          )}

          {state === "done" && (
            <>
              <h1 className="font-display text-4xl text-foreground">
                Peruutus vahvistettu
              </h1>
              <p className="mt-4 text-muted-foreground">
                Emme lähetä sinulle enää sähköposteja. Tervetuloa takaisin
                milloin tahansa.
              </p>
              <Link
                to="/"
                className="mt-8 inline-flex rounded-full border border-border px-6 py-3 text-sm"
              >
                Palaa etusivulle
              </Link>
            </>
          )}

          {state === "already" && (
            <>
              <h1 className="font-display text-4xl text-foreground">
                Peruutus on jo voimassa
              </h1>
              <p className="mt-4 text-muted-foreground">
                Tämä osoite on jo poistettu lähetyslistalta – sinulle ei
                lähetetä enää sähköposteja.
              </p>
              <Link
                to="/"
                className="mt-8 inline-flex rounded-full border border-border px-6 py-3 text-sm"
              >
                Palaa etusivulle
              </Link>
            </>
          )}

          {(state === "invalid" || state === "error") && (
            <>
              <h1 className="font-display text-4xl text-foreground">
                {state === "invalid"
                  ? "Linkki ei ole voimassa"
                  : "Peruutus epäonnistui"}
              </h1>
              <p className="mt-4 text-muted-foreground">
                {state === "invalid"
                  ? "Peruutuslinkki on virheellinen tai vanhentunut. Käytä sähköpostissa olevaa tuoreinta linkkiä."
                  : "Jotain meni pieleen. Yritä hetken kuluttua uudelleen."}
              </p>
              <Link
                to="/"
                className="mt-8 inline-flex rounded-full border border-border px-6 py-3 text-sm"
              >
                Palaa etusivulle
              </Link>
            </>
          )}
        </div>
      </section>
    </Layout>
  );
}